import queryString from "query-string";
import React from "react";
import {
    Button,
    Card,
    Form,
    InputGroup,
    Modal,
    OverlayTrigger,
    Spinner,
    Tooltip,
} from "react-bootstrap";
import { FaPlay } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router";

import * as api from "../../api/api";
import {
    useDockCollection,
    useLocationDocument,
    useTaskDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CorvusSetting from "../../components/CorvusSetting";
import DroneSelector from "../../components/DroneSelector";
import Layout, { Title } from "../../components/Layout/Layout";
import CorvusMap from "../../components/Map/index.jsx";
import MapOptimizationAlert from "../../components/MapOptimizationAlert/index.jsx";
import TaskSelector from "../../components/TaskSelector";
import { useUser } from "../../contexts/user_provider.jsx";
import urls from "../../urls.jsx";

function DisabledButtonTooltip({ reason, children }) {
    if (!reason) return children;

    return (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="disabled-start-reason">{reason}</Tooltip>}
        >
            <span className="d-inline-block">{children}</span>
        </OverlayTrigger>
    );
}

export default function StartPage() {
    const { getUserIsAdmin } = useUser();
    const { organizationId, locationId } = useParams();
    const location = useLocation();
    const { droneId, taskId } = queryString.parse(location.search);
    const [task] = useTaskDocument(organizationId, locationId, taskId);
    const [locationDoc] = useLocationDocument(organizationId, locationId);
    const [docks] = useDockCollection(organizationId, locationId);
    const precomputeProgress =
        locationDoc?.routePlanner?.precomputeProgress ?? 1.0;
    const isPrecomputing = precomputeProgress < 1.0;
    const navigate = useNavigate();
    const [startModalVisible, setStartModalVisible] = React.useState(false);
    const [taskPreviewSuccess, setTaskPreviewSuccess] = React.useState(true);
    const [isStartingTask, setIsStartingTask] = React.useState(false);
    const [inspectMap, setInspectMap] = React.useState(false);
    const [previewStartSource, setPreviewStartSource] =
        React.useState("custom");
    const [customPreviewStart, setCustomPreviewStart] = React.useState(null);
    const [customPreviewStartEnabled, setCustomPreviewStartEnabled] =
        React.useState(false);
    const [selectedDroneUnavailableReason, setSelectedDroneUnavailableReason] =
        React.useState(null);
    const previewUsesPreset =
        !droneId || droneId === "none" || droneId === "ANY";
    const previewRequiresCustomStart =
        previewUsesPreset || Boolean(selectedDroneUnavailableReason);
    const useCustomPreviewStart =
        previewRequiresCustomStart || customPreviewStartEnabled;
    const previewStartPosition = React.useMemo(() => {
        if (previewStartSource.startsWith("dock:")) {
            const dockId = previewStartSource.slice("dock:".length);
            return docks.find((dock) => dock.id === dockId)?.position;
        }

        return customPreviewStart;
    }, [customPreviewStart, docks, previewStartSource]);

    function hideStartModal() {
        setStartModalVisible(false);
    }

    function showStartModal() {
        setStartModalVisible(true);
    }

    function onTaskChange(newTaskId) {
        let startUrl = `${urls.locationStart(organizationId, locationId)}?`;
        startUrl += `taskId=${newTaskId || "none"}`;
        startUrl += `&droneId=${droneId || "none"}`;
        navigate(startUrl);
    }

    function onDroneChange(newDroneId) {
        let startUrl = `${urls.locationStart(organizationId, locationId)}?`;
        startUrl += `taskId=${taskId || "none"}`;
        startUrl += `&droneId=${newDroneId || "none"}`;
        navigate(startUrl);
    }

    function onPreviewMapClick(position) {
        if (!useCustomPreviewStart || previewStartSource !== "custom") return;

        setCustomPreviewStart({ x: position.x, y: position.y, z: 0 });
    }

    function startTask() {
        if (isStartingTask) return;
        setIsStartingTask(true);

        let redirectUrl = urls.drone(droneId);
        if (droneId === "ANY") {
            redirectUrl = urls.location(organizationId, locationId);
        }

        const promise = api
            .startMissionHandler(organizationId, locationId, taskId, droneId)
            .finally(() => {
                hideStartModal();
                navigate(redirectUrl);
                setIsStartingTask(false); // Reset state when task finishes
            });

        return promise;
    }

    function getStartDisabledReason() {
        if (!droneId || droneId === "none") {
            return "Select a drone before starting.";
        }
        if (selectedDroneUnavailableReason) {
            return selectedDroneUnavailableReason;
        }
        if (useCustomPreviewStart) {
            return "Custom starting points are for preview only.";
        }
        if (!taskId || taskId === "none") {
            return "Select a task before starting.";
        }
        if (task && !task.commands?.length) {
            return "The selected task has no commands.";
        }
        if (isPrecomputing) {
            return "Map optimization is still in progress.";
        }
        if (!taskPreviewSuccess) {
            return "The task preview could not be created.";
        }
        return null;
    }

    const startDisabledReason = getStartDisabledReason();

    return (
        <Layout>
            <title>Start Mission | MyCorvus</title>
            <Breadcrumbs path={breadcrumbPaths.start} location={locationDoc} />
            <Modal show={startModalVisible} onHide={hideStartModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Start</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Start task <b>{task?.name}</b> on Drone <b>{droneId}</b>{" "}
                    now?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={hideStartModal}
                    >
                        Cancel
                    </Button>
                    <DisabledButtonTooltip reason={startDisabledReason}>
                        <Button
                            variant="primary"
                            onClick={startTask}
                            disabled={
                                Boolean(startDisabledReason) || isStartingTask
                            }
                            style={
                                startDisabledReason
                                    ? { pointerEvents: "none" }
                                    : undefined
                            }
                        >
                            {isStartingTask ? (
                                <>
                                    <Spinner
                                        animation="border"
                                        role="status"
                                        variant="outline-primary"
                                        size="sm"
                                        className="me-1"
                                    >
                                        <span className="visually-hidden">
                                            Starting...
                                        </span>
                                    </Spinner>
                                    Starting
                                </>
                            ) : (
                                <>
                                    <FaPlay
                                        className="me-2"
                                        style={{ marginTop: "-2px" }}
                                    />
                                    Start
                                </>
                            )}
                        </Button>
                    </DisabledButtonTooltip>
                </Modal.Footer>
            </Modal>
            <Card>
                <Title
                    title={"Start"}
                    BackButton
                    to={urls.location(organizationId, locationId)}
                />
                <Card.Body className="px-4 pt-0">
                    <Form>
                        <Form.Group>
                            <InputGroup>
                                <CorvusSetting name="Drone" className="mb-2">
                                    <DroneSelector
                                        value={droneId || "none"}
                                        onChange={onDroneChange}
                                        allowUnavailable
                                        onSelectedDroneUnavailableReasonChange={
                                            setSelectedDroneUnavailableReason
                                        }
                                    />
                                </CorvusSetting>
                                <CorvusSetting name="Task" className="mb-2">
                                    <TaskSelector
                                        value={taskId || "none"}
                                        onChange={onTaskChange}
                                    />
                                </CorvusSetting>
                            </InputGroup>
                            <Form.Check
                                type="switch"
                                id="use-custom-preview-start"
                                label="Use custom preview start"
                                checked={useCustomPreviewStart}
                                disabled={previewRequiresCustomStart}
                                onChange={(event) =>
                                    setCustomPreviewStartEnabled(
                                        event.target.checked,
                                    )
                                }
                                className="mb-2"
                            />
                            {useCustomPreviewStart && (
                                <>
                                    <CorvusSetting
                                        name="Preview start"
                                        className="mb-2"
                                    >
                                        <Form.Select
                                            value={previewStartSource}
                                            onChange={(event) =>
                                                setPreviewStartSource(
                                                    event.target.value,
                                                )
                                            }
                                        >
                                            {docks.map((dock) => (
                                                <option
                                                    key={dock.id}
                                                    value={`dock:${dock.id}`}
                                                >
                                                    {`Dock: ${dock.name || dock.id}`}
                                                </option>
                                            ))}
                                            <option value="custom">
                                                Custom map point
                                            </option>
                                        </Form.Select>
                                    </CorvusSetting>
                                    {previewStartSource === "custom" && (
                                        <Form.Text className="d-block mb-2">
                                            {customPreviewStart
                                                ? `Selected x ${customPreviewStart.x.toFixed(2)}, y ${customPreviewStart.y.toFixed(2)}, z 0`
                                                : "Select a point on the map below."}
                                        </Form.Text>
                                    )}
                                </>
                            )}
                            <MapOptimizationAlert
                                organizationId={organizationId}
                                locationId={locationId}
                            />
                            <DisabledButtonTooltip reason={startDisabledReason}>
                                <Button
                                    className="me-2"
                                    variant="primary"
                                    onClick={showStartModal}
                                    disabled={Boolean(startDisabledReason)}
                                    style={
                                        startDisabledReason
                                            ? { pointerEvents: "none" }
                                            : undefined
                                    }
                                >
                                    <FaPlay
                                        className="me-2"
                                        style={{ marginTop: "-2px" }}
                                    />
                                    Start
                                </Button>
                            </DisabledButtonTooltip>
                            {getUserIsAdmin() && (
                                <Form.Check
                                    type="switch"
                                    id="Set inspect map"
                                    label={"Inspect map"}
                                    checked={inspectMap}
                                    onChange={(e) =>
                                        setInspectMap(e.target.checked)
                                    }
                                    className="mt-3"
                                />
                            )}
                        </Form.Group>
                    </Form>
                </Card.Body>
            </Card>
            <Card className="mt-4" style={{ height: "50vh" }}>
                <CorvusMap
                    organizationId={organizationId}
                    locationId={locationId}
                    taskPreview={{
                        taskId,
                        droneId,
                        useStartPosition: useCustomPreviewStart,
                        startPosition: useCustomPreviewStart
                            ? previewStartPosition
                            : undefined,
                    }}
                    setTaskPreviewSuccess={setTaskPreviewSuccess}
                    inspect={inspectMap}
                    point={
                        useCustomPreviewStart ? previewStartPosition : undefined
                    }
                    lockFocusOnPoint={false}
                    onMapClick={onPreviewMapClick}
                />
            </Card>
        </Layout>
    );
}
